import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, MapPin, Clock, ChevronRight, Sparkles, HandHeart, Newspaper, Instagram, Repeat, Images, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PublicHeader } from "@/components/PublicHeader";
import { InstallHintBanner } from "@/components/InstallHintBanner";
import { NotificationsOptIn } from "@/components/NotificationsOptIn";
import { VerseCard } from "@/components/VerseCard";
import { GallerySlideshow } from "@/components/GallerySlideshow";
import { SurveyBanner } from "@/components/SurveyBanner";
import { NewsDetailModal } from "@/components/NewsDetailModal";
import { useI18n } from "@/contexts/I18nContext";
import { useMember } from "@/contexts/MemberContext";
import { api } from "@/lib/api";
import { fmtDate, pickLang, fmtTime12 } from "@/lib/format";
import { markConsumed } from "@/lib/notificationRecovery";

export default function HomePage() {
  const { t, lang } = useI18n();
  const { member } = useMember();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ upcoming_events: [], news: [], settings: {} });
  const [selectedNews, setSelectedNews] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/home");
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) console.error("HomePage: failed to load home payload", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /**
   * Deep-link handler: when a member taps a news push notification, the
   * service worker opens ``/?news=<id>``. We watch that query param, fetch
   * the news item, open the detail modal, then strip the param from the URL
   * so a subsequent refresh doesn't re-open the modal.
   *
   * Mirrors ``BroadcastModal``'s ``?broadcast=<id>`` deep-link pattern for
   * consistency.
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newsId = params.get("news");
    if (!newsId) return;
    if (selectedNews && selectedNews.id === newsId) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/news/${newsId}`);
        if (!cancelled) {
          setSelectedNews(data);
          // News was fetched and about to be shown — clear the recovery
          // record so no "you missed a notification" banner appears
          // later for content the user actually saw.
          markConsumed("news");
        }
      } catch (err) {
        if (!cancelled) console.error("HomePage: failed to load deep-linked news", err);
      } finally {
        // Always clean the URL so refresh doesn't re-open (and so a 404
        // doesn't loop forever attempting to fetch a deleted news item).
        // Also strip the ``nt=1`` notification-tap marker that the SW
        // now appends to every push target -- once we've consumed the
        // deep link there's no reason to keep it in the address bar.
        if (!cancelled) {
          const nextParams = new URLSearchParams(location.search);
          nextParams.delete("news");
          nextParams.delete("nt");
          const qs = nextParams.toString();
          navigate(
            { pathname: location.pathname, search: qs ? `?${qs}` : "" },
            { replace: true }
          );
        }
      }
    })();
    return () => { cancelled = true; };
  }, [location.search, location.pathname, navigate, selectedNews]);

  return (
    <div>
      <PublicHeader subtitle={t.tagline} />
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="px-4 pt-4 space-y-4"
      >
        {/* Hero greeting */}
        <Card className="overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/5">
          <div
            className="relative px-5 py-6"
            style={{
              background:
                "radial-gradient(700px circle at 90% 10%, rgba(200,30,58,0.10) 0%, transparent 55%), linear-gradient(180deg, hsl(var(--accent)) 0%, hsl(var(--card)) 100%)",
            }}
          >
            <div className="flex items-start gap-2">
              <Sparkles className="mt-1 h-4 w-4 text-primary" />
              <p className="text-xs uppercase tracking-widest text-muted-foreground">{t.home.welcomeCardTitle}</p>
            </div>
            <h1 className="font-serif-display mt-1 text-2xl font-semibold leading-tight" data-testid="home-hero-greeting">
              {t.home.greeting}{member?.full_name ? `, ${member.full_name.split(" ")[0]}` : ""}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">{t.home.welcomeCardBody}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button asChild size="sm" className="rounded-full" data-testid="home-cta-give">
                <Link to="/give"><HandHeart className="mr-1 h-4 w-4" />{t.nav.give}</Link>
              </Button>
              <Button asChild size="sm" variant="outline" className="rounded-full bg-white" data-testid="home-cta-events">
                <Link to="/events"><CalendarDays className="mr-1 h-4 w-4" />{t.nav.events}</Link>
              </Button>
            </div>
          </div>
        </Card>

        {/* Hebrews 13:8 verse \u2014 sits right below the greeting */}
        <VerseCard />

        {/* Admin-controlled survey banner (e.g., Cornerstone Project) */}
        <SurveyBanner settings={data.settings} />

        {/* Gallery auto-advancing slideshow (hidden when empty) */}
        <GallerySlideshow />

        <InstallHintBanner />
        <NotificationsOptIn />

        {/* Upcoming events */}
        <section className="space-y-3" data-testid="home-upcoming-section">
          <SectionHeader
            icon={CalendarDays}
            title={t.home.upcoming}
            actionTo="/events"
            actionLabel={t.home.viewAll}
          />
          {loading ? (
            <SkeletonList />
          ) : data.upcoming_events.length === 0 ? (
            <EmptyCard text={t.home.emptyEvents} />
          ) : (
            <div className="space-y-3">
              {data.upcoming_events.slice(0, 4).map((ev) => (
                <EventCard key={ev.id} ev={ev} lang={lang} />
              ))}
            </div>
          )}
        </section>

        {/* Latest news */}
        <section className="space-y-3 pt-2" data-testid="home-news-section">
          <SectionHeader icon={Newspaper} title={t.home.latestNews} />
          {loading ? (
            <SkeletonList />
          ) : data.news.length === 0 ? (
            <EmptyCard text={t.home.emptyNews} />
          ) : (
            <div className="space-y-3">
              {data.news.slice(0, 4).map((n) => (
                <NewsCard key={n.id} n={n} lang={lang} onOpen={() => setSelectedNews(n)} />
              ))}
            </div>
          )}
        </section>

        {/* Instagram CTA */}
        {data.settings?.instagram && (
          <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Instagram className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">Instagram</p>
                <p className="text-xs text-muted-foreground truncate">@iglesia_ncl</p>
              </div>
              <Button asChild size="sm" variant="outline" className="rounded-full" data-testid="home-instagram-cta">
                <a href={data.settings.instagram} target="_blank" rel="noreferrer">{t.social.visit}</a>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Gallery entry card \u2014 always visible so users have a path to /gallery
            even when the slideshow above is empty. */}
        <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm" data-testid="home-gallery-link-card">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Images className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">{t.home.gallerySectionTitle}</p>
              <p className="text-xs text-muted-foreground truncate">{t.gallery?.title || t.home.gallerySectionTitle}</p>
            </div>
            <Button asChild size="sm" variant="outline" className="rounded-full" data-testid="home-gallery-link-button">
              <Link to="/gallery">{t.home.viewAll}<ChevronRight className="ml-0.5 h-3.5 w-3.5" /></Link>
            </Button>
          </CardContent>
        </Card>

        <div className="h-2" />
      </motion.div>

      <NewsDetailModal item={selectedNews} onClose={() => setSelectedNews(null)} />
    </div>
  );
}

function SectionHeader({ icon: Icon, title, actionTo, actionLabel }) {
  return (
    <div className="flex items-end justify-between gap-3 px-1">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <h2 className="font-serif-display text-lg font-semibold tracking-tight">{title}</h2>
      </div>
      {actionTo && (
        <Link to={actionTo} className="text-xs font-medium text-primary inline-flex items-center gap-0.5" data-testid="home-view-all-events">
          {actionLabel}<ChevronRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 2 }).map((_, i) => (
        <Skeleton key={i} className="h-20 w-full rounded-2xl" />
      ))}
    </div>
  );
}

function EmptyCard({ text }) {
  return (
    <Card className="rounded-2xl border-dashed bg-white/60" data-testid="empty-state-card">
      <CardContent className="py-6 text-center text-sm text-muted-foreground">{text}</CardContent>
    </Card>
  );
}

function EventCard({ ev, lang }) {
  const { t } = useI18n();
  const title = pickLang(ev, "title", lang);
  return (
    <Card className="relative overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/5 before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-primary">
      <CardContent className="p-4 pl-5">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-serif-display text-base font-semibold leading-tight" data-testid="event-card-title">{title}</p>
          {ev.recurring_weekly && (
            <Badge variant="secondary" className="gap-1 text-[10px]" data-testid={`event-card-recurring-${ev.id}`}>
              <Repeat className="h-3 w-3" />
              {t.admin?.eventsForm?.recurringBadge || (lang === "es" ? "Cada semana" : "Weekly")}
            </Badge>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(ev.event_date, lang)}</span>
          {ev.event_time && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{fmtTime12(ev.event_time)}</span>}
          {ev.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{ev.location}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

function NewsCard({ n, lang, onOpen }) {
  const { t } = useI18n();
  const title = pickLang(n, "title", lang);
  const body = pickLang(n, "body", lang);
  const hasReg = !!(n.registration_url || "").trim();
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group block w-full text-left rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 transition-transform active:scale-[0.99]"
      data-testid={`news-card-${n.id}`}
      aria-label={`${title} - ${t.events?.tapForDetails || (lang === "es" ? "Toca para ver detalles" : "Tap to see details")}`}
    >
      <Card className="overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/5 group-hover:shadow-md transition-shadow">
        {n.image_url && (
          <div className="relative bg-muted">
            <img
              src={n.image_url}
              alt={title || ""}
              loading="lazy"
              className="block max-h-[420px] w-full object-cover"
              data-testid={`news-card-image-${n.id}`}
            />
          </div>
        )}
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-serif-display text-base font-semibold leading-tight" data-testid="news-card-title">{title}</p>
                {hasReg && (
                  <Badge className="gap-1 text-[10px] bg-primary/15 text-primary hover:bg-primary/20" data-testid={`news-has-registration-${n.id}`}>
                    <ExternalLink className="h-3 w-3" />
                    {t.events?.registerCta || (lang === "es" ? "Registrarse" : "Register")}
                  </Badge>
                )}
              </div>
              {body && <p className="mt-1 text-sm text-muted-foreground line-clamp-4 whitespace-pre-wrap">{body}</p>}
              <p className="mt-2 text-[11px] uppercase tracking-wider text-muted-foreground">{fmtDate(n.news_date || n.created_at, lang)}</p>
            </div>
            <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
          </div>
        </CardContent>
      </Card>
    </button>
  );
}
